import React from 'react';
import { DownOutlined } from '@ant-design/icons';
import HumanPullArt from './HumanPullArt';
import css from './HumanPullHero.module.scss';

interface HumanPullHeroProps {
  expanded: boolean;
  onCollapse: () => void;
  onExpand: () => void;
}

/**
 * The thesis. New users (or anyone who re-opens it) get the full explainer with
 * the pull-track art; once they've completed the loop once it collapses to a
 * single quiet line. The Dashboard owns the expanded/collapsed decision so the
 * adaptive rule and analytics live in one place.
 */
const HumanPullHero: React.FC<HumanPullHeroProps> = ({ expanded, onCollapse, onExpand }) => {
  if (!expanded) {
    return (
      <button type="button" className={css.collapsedBar} onClick={onExpand}>
        <span className={css.collapsedText}>You reach people, not job boards.</span>
        <span className={css.collapsedToggle}>
          How it works <DownOutlined className={css.collapsedChevron} />
        </span>
      </button>
    );
  }

  return (
    <section className={css.hero} aria-label="How Job Beast works">
      <div className={css.copy}>
        <p className={css.eyebrow}>How Job Beast works</p>
        <h1 className={css.headline}>
          Reach the person who decides,
          <br />
          not a job board.
        </h1>
        <div className={css.subline}>
          <p>Apply online and you're screened by software, by bias, by the wrong people.</p>
          <p>The person who can actually judge you rarely sees you.</p>
          <p>So we find that person, draft a tailored message, and you send it.</p>
          <p>
            <strong>It's a numbers game:</strong> most people send <strong>10–20</strong> before
            their first interview.
          </p>
        </div>

        <div className={css.stats}>
          <div className={css.statGroup}>
            <p className={css.statGroupLabel}>Applying online</p>
            <div className={css.statRows}>
              <div className={css.stat}>
                <span className={`${css.statNum} ${css.bad}`}>3%</span>
                <span className={css.statLabel}>reach an interview</span>
              </div>
              <div className={css.stat}>
                <span className={`${css.statNum} ${css.bad}`}>88%</span>
                <span className={css.statLabel}>
                  of employers admit strong candidates get filtered out
                </span>
              </div>
            </div>
            <p className={css.statSource}>Industry figures — not ours</p>
          </div>

          <div className={css.statGroup}>
            <p className={css.statGroupLabel}>Reaching the decision-maker</p>
            <div className={css.statRows}>
              {/* The measured Ashby figure (38M applications), with the two
                  qualifiers its source attaches: a GENUINELY ENDORSED referral,
                  at the INTERVIEW stage. The old "3× to 16×" ceiling came from
                  the evidence base's warm-intro row, which that doc marks
                  "○ estimate — No dataset". Keep this identical to the drawer
                  and the applications empty state — one claim, one number. */}
              <div className={css.stat}>
                <span className={`${css.statNum} ${css.good}`}>up to ~13×</span>
                <span className={css.statLabel}>
                  more likely to reach an interview, with a genuine endorsement
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className={css.actions}>
          <button type="button" className={css.gotIt} onClick={onCollapse}>
            Got it
          </button>
          <a
            className={css.playbookLink}
            href="https://www.jobbeast.org/playbook"
            target="_blank"
            rel="noopener noreferrer"
          >
            Read the playbook →
          </a>
        </div>
      </div>
      <div className={css.artWrap} aria-hidden="true">
        <HumanPullArt />
      </div>
    </section>
  );
};

export default HumanPullHero;
